import { Table, TableColumnType } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { Response } from '../../api/util/with-response-formatter-interceptor'
import { Product } from '../../models/models'
import { toastFailure } from '../../util/toast'
import useDebounce from '../project-master/single-project-view/hooks'

interface Props {
    pagination?: boolean
    //antd ColumnType<T> does not have title property
    columns?: Array<TableColumnType<Product>>
    actionComponents?: Array<(product: Product) => JSX.Element>
    dependencies?: any
    actionColumnTitle?: string
    request: (pagination?: {
        skip: number
        limit: number
    }) => Promise<Response<{ count: number; entities: Product[] }>>
}

export const ProductsTableColumns: { [key in string]: TableColumnType<Product> } = {
    Id: {
        title: 'Id',
        key: 'id',
        render: (product: Product) => {
            return product.id
        },
    },
    Name: {
        title: 'Name',
        key: 'name',
        render: (product: Product) => {
            return product.name
        },
    },
    Code: {
        title: 'code',
        key: 'Code',
        width: '250px',
        render: (product: Product) => {
            return product.code
        },
    },
    Description: {
        title: 'Description',
        key: 'Description',
        render: (product: Product) => {
            return product.description
        },
    },
    ProductGroup: {
        title: 'Product Group',
        key: 'productGroup',
        render: (product: Product) => {
            return product.productGroupCode
        },
    },
} as const

const DEFAULT_COLUMNS = [
    ProductsTableColumns.Id,
    ProductsTableColumns.Name,
    ProductsTableColumns.ProductGroup,
]

export default function ProductsTable({
    request,
    columns,
    pagination,
    actionComponents,
    dependencies,
    actionColumnTitle,
}: Props) {
    const [products, setProducts] = useState<Product[]>([])
    const [productsAmount, setProductsAmount] = useState<number>(0)
    const rowsPerPageOptions = [10, 20, 50, 100]
    type RowsPerPageOptions = typeof rowsPerPageOptions[number] // 10 | 20 | 50 | 100
    const [page, setPage] = useState<number>(1)
    const [rowsPerPage, setRowsPerPage] = useState<RowsPerPageOptions>(10)

    const getProductsDeps = useDebounce(JSON.stringify(dependencies), 200)

    const pageChange = (page: number, pageSize: number) => {
        setPage(page)
        setRowsPerPage(pageSize)
    }

    const tableColumns = useMemo(() => {
        let cols = columns ? Object.values(columns) : DEFAULT_COLUMNS
        if (actionComponents) {
            cols = [
                ...cols,
                {
                    title: actionColumnTitle ?? 'Actions',
                    key: 'actions',
                    render: (product: Product) => {
                        return (<>{actionComponents.map((a) => a(product))}</>) as any
                    },
                },
            ]
        }
        return cols
    }, [columns])

    const getProducts = async (resetPage = false) => {
        const response = await request({
            skip: page * rowsPerPage - rowsPerPage,
            limit: rowsPerPage,
        })

        if (response.successful) {
            const { count, entities } = response.data
            resetPage && setPage(1)
            setProductsAmount(count)
            setProducts(entities)
        } else {
            toastFailure(response.message)
        }
    }

    // this happens when dependencies change outside the table, e.g filters given in with getProductsDeps
    useEffect(() => {
        getProducts(true)
    }, [getProductsDeps, rowsPerPage])

    // this happens when dependencies change outside the table, e.g filters given in with getProductsDeps
    useEffect(() => {
        getProducts()
    }, [page])

    return (
        <Table
            rowKey="id"
            columns={tableColumns}
            dataSource={products}
            expandable={undefined}
            size="small"
            pagination={
                pagination
                    ? {
                          defaultPageSize: 10,
                          current: page,
                          onChange: pageChange,
                          total: productsAmount,
                      }
                    : false
            }
        />
    )
}
